import React, { useState, useRef, useEffect } from "react";
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  Platform,
} from "react-native";
import { useTranslation } from "react-i18next";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";

const HARDCODED_PIN = "6651";

type MapiPinInputStepProps = {
  onContinue?: () => void;
  resetSignal?: number;
  testID?: string | undefined;
};

export default function MapiPinInputStep({
  onContinue,
  resetSignal = 0,
  testID,
}: MapiPinInputStepProps) {
  const { t } = useTranslation();
  const [pin, setPin] = useState(["", "", "", ""]);
  const [pinError, setPinError] = useState("");
  const inputs = useRef<(TextInput | null)[]>([]);
  const [isFocused, setIsFocused] = useState(false);

  const borderColor = isFocused
    ? Colors.black
    : Colors.textInputBackground;

  useEffect(() => {
    setPin(["", "", "", ""]);
    setPinError("");
    inputs.current[0]?.focus();
  }, [resetSignal]);

  const handleChange = (text: string, index: number) => {
    const isValid =
      text.length === 0 ||
      text.split("").every((char) => {
        const charCode = char.charCodeAt(0);
        return charCode >= 48 && charCode <= 57;
      });

    if (!isValid) return;

    const newPin = [...pin];
    newPin[index] = text;
    setPin(newPin);
    setPinError("");

    if (text && index < 3) {
      inputs.current[index + 1]?.focus();
    }
    if (!text && index > 0) {
      inputs.current[index - 1]?.focus();
    }

    if (newPin.every((d) => d !== "")) {
      const entered = newPin.join("");
      if (entered === HARDCODED_PIN) {
        setTimeout(() => onContinue?.(), 200);
      } else {
        setPinError("Incorrect PIN. Please try again.");
        setTimeout(() => {
          setPin(["", "", "", ""]);
          inputs.current[0]?.focus();
        }, 500);
      }
    }
  };

  const errorBorderColor = pinError ? Colors.errorRedColor : borderColor;

  return (
    <View style={styles.container} testID={testID}>
      <Text style={styles.title}>{t("PIN_INPUT_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("PIN_INPUT_SUBTITLE")}</Text>

      <View style={styles.pinContainer}>
        {pin.map((digit, index) => (
          <View
            key={index}
            style={[
              styles.pinBox,
              {
                borderColor: errorBorderColor,
                borderWidth: pinError ? 1 : 1,
              },
            ]}
          >
            <TextInput
              testID={`mapiPinInput${index}`}
              ref={(el) => (inputs.current[index] = el)}
              autoFocus={index === 0}
              style={[
                styles.pinInput,
                {
                  borderColor: errorBorderColor,
                  borderWidth: pinError ? 1 : 1,
                },
              ]}
              keyboardType="number-pad"
              maxLength={1}
              secureTextEntry
              value={digit}
              onChangeText={(text) => handleChange(text, index)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            />
          </View>
        ))}
      </View>

      {!!pinError && <Text style={styles.errorText}>{pinError}</Text>}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8,
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: "400",
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 32,
  },
  pinContainer: {
    flexDirection: "row",
    marginBottom: 8,
    justifyContent: "space-between",
  },
  pinBox: {
    width: 56,
    height: 56,
    borderRadius: 8,
    textAlign: "center",
  },
  pinInput: {
    borderRadius: 8,
    flex: 1,
    textAlign: "center",
    fontSize: 22,
    fontWeight: "600",
    backgroundColor: Colors.searchCompanyBackground,
    ...(Platform.OS === "web" &&
      ({
        outlineWidth: 0,
        outline: "none",
        border: "none",
        boxShadow: "none",
      } as any)),
  },
  errorText: {
    fontSize: 14,
    fontFamily: "Rubik",
    color: Colors.errorRedColor,
    fontWeight: "400",
    lineHeight: 20,
    marginTop: 4,
  },
});
